import { Component, Input, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { api_routes, db_tables, features } from '@app/consts';
import { FeatureService } from '@app/core/services/feature.service';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { BuySellPlanReviewer, BuySellPlanReviewerSubmit } from '@app/modules/time-off/models/buysell.model';
import { BuySellService } from '@app/modules/time-off/services/buy-sell.service';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { FormGeneratorDialogComponent } from '@app/shared/components/form-generator-dialog/form-generator-dialog.component';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { SecurityProtectedBase } from '@app/shared/components/security-protected/security-protected';
import { GridState } from '@app/shared/services/grid-state/grid-state.model';
import { TranslateService } from '@ngx-translate/core';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { defer, forkJoin } from 'rxjs';

@Component({
  selector: 'app-buy-sell-plan-reviewers',
  templateUrl: './buy-sell-plan-reviewers.component.html',
  styleUrls: ['./buy-sell-plan-reviewers.component.scss']
})
export class BuySellPlanReviewersComponent extends SecurityProtectedBase implements OnInit {
  @Input() buySellPlanId: string;

  public columns: any[] = [
    { field: "reviewerType", subField: "name", title: "Reviewer Type", localizationCode: 'ReviewerType' },
    { field: "reviewer", title: "Reviewer", type: 'employee', localizationCode: 'Reviewer' },
    { field: "orderNumber", title: "Order Number", localizationCode: 'OrderNumber' }
  ];

  filterCategories: any[] = [];

  sortableColumns: any[] = [
    { field: "reviewer", sortValue: ["reviewer.FirstName", "reviewer.LastName"] },
    { field: "reviewer", sortValue: "reviewer.name" },
    { field: "orderNumber", sortValue: "orderNumber" },
  ];

  public gridDataResult: GridDataResult;
  public api_routes = api_routes;
  public db_tables = db_tables;
  isLoading: boolean = true;
  pageSize: number = 20;
  skip: number = 0;
  clearSelectedItems: boolean = false;
  formValid: any;
  sortString: string = 'orderNumber-asc';
  isLoadingPermissions: boolean = true;
  filterString: string;
  gridId: string;
  getRequest: any;
  searchValue: string;
  searchFilterString: string;
  dialogRef: any;
  sort: SortDescriptor[] = [
    { field: 'orderNumber', dir: 'asc' }
  ];

  constructor(
    private overlayService: OverlayService,
    public translate: TranslateService,
    private snackbarService: SnackbarService,
    private router: Router,
    private dialog: MatDialog,
    private buySellService: BuySellService,
    featureService: FeatureService,
  ) {
    super(featureService, features.BUY_SELL);
  }

  ngOnInit(): void {
    this.gridId = `buy-sell-plan-reviewers-${this.buySellPlanId}`;
    if (this.hasFeatureAccess) {
      this.getBuySellPlanReviewers();
    }
  }

  getBuySellPlanReviewers() {
    this.isLoading = true;

    // Combine filterString and searchFilterString if both exist
    let baseFilter = `(plan.id = "${this.buySellPlanId}")`;
    let combinedFilter;

    if (this.filterString && this.searchFilterString) {
      combinedFilter = `${baseFilter} AND (${this.filterString}) AND (${this.searchFilterString})`;
    } else if (this.filterString) {
      combinedFilter = `${baseFilter} AND (${this.filterString})`;
    } else if (this.searchFilterString) {
      combinedFilter = `${baseFilter} AND (${this.searchFilterString})`;
    } else {
      combinedFilter = baseFilter;
    }
    // Use combinedFilter in the service call

    this.getRequest = this.buySellService.getBuySellPlanReviewers(this.pageSize, this.skip, combinedFilter, this.sortString)
      .subscribe(
        {
          next: (v) => {
            this.gridDataResult = {
              data: v.data,
              total: v.total
            };

            this.isLoading = false;
          },
          error: (e) => this.isLoading = false,
          complete: () => this.isLoading = false
        }
      );
  }

  openAddReviewersDialog(reviewer?: BuySellPlanReviewer) {
    let formData = {
      id: reviewer ? reviewer.id : null,
      reviewerType: reviewer ? reviewer.reviewerType?.id : null,
      reviewer: reviewer ? reviewer.reviewer?.id : null,
      orderNumber: reviewer ? reviewer.orderNumber : null
    }

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      formId: 'frm_ESV6999UZbCpQf',
      formData: formData
    };

    this.dialogRef = this.dialog.open(FormGeneratorDialogComponent, dialogConfig);

    const sub = this.dialogRef.componentInstance.emitFormData.subscribe((event) => {
      this.addReviewers(event);
      sub.unsubscribe();
    });
  }

  addReviewers(formData: any) {
    this.overlayService.show();

    const buySellPlanReviewerSubmit: BuySellPlanReviewerSubmit = {
      changeReason: null,
      changeReasonComments: null,
      originalCreatedOn: null,
      asOf: null,
      reviewerType: {
        id: formData.reviewerType
      },
      reviewer: formData.reviewer ? { id: formData.reviewer } : null,
      orderNumber: formData.orderNumber,
      plan: {
        id: this.buySellPlanId
      }
    }

    if(formData.id){
      this.buySellService.editBuySellPlanReviewer(formData.id, buySellPlanReviewerSubmit)
        .subscribe(
          {
            next: (v) => {
              this.snackbarService.openSnackBar(`${this.translate.instant('ReviewersAddedSuccessfully')}`, 'clear', 'success');
              this.getBuySellPlanReviewers();
              this.dialogRef.close();
            },
            error: (e) => this.overlayService.hide(),
            complete: () => this.overlayService.hide()
          }
        );
    }
    else {
      this.buySellService.addBuySellPlanReviewer(buySellPlanReviewerSubmit)
        .subscribe(
          {
            next: (v) => {
              this.snackbarService.openSnackBar(`${this.translate.instant('ReviewersAddedSuccessfully')}`, 'clear', 'success');
              this.getBuySellPlanReviewers();
              this.dialogRef.close();
            },
            error: (e) => this.overlayService.hide(),
            complete: () => this.overlayService.hide()
          }
        );
    }

  }

  openConfirmDeleteDialog(reviewerIds: string[]) {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      text: this.translate.instant('AreYouSureYouWantToDeleteThis')
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(
      data => {
        if (data === true) {
          this.deleteBuySellReviewers(reviewerIds);
        }
      }
    );
  }

  deleteBuySellReviewers(buySellPlanIds: string[]) {
    const observables = buySellPlanIds.map(selectedItem => defer(() => this.buySellService.deleteBuySellPlanReviewer(selectedItem)));

    this.overlayService.show();

    forkJoin(observables)
      .subscribe(
        {
          next: (v) => {
            this.clearSelectedItems = !this.clearSelectedItems;
            this.snackbarService.openSnackBar(`${this.translate.instant('DeletedSuccessfully')}`, 'clear', 'success');
            this.getBuySellPlanReviewers();
          },
          error: (e) => this.overlayService.hide(),
          complete: () => this.overlayService.hide()
        }
      );
  }

  gridStateChange(gridState: GridState) {
    if (this.pageSize !== gridState.pageSize) {
      this.pageSize = gridState.pageSize;
    }
    if (this.skip !== gridState.skip) {
      this.skip = gridState.skip;
    }
    if (this.sortString !== gridState.sortString) {
      this.sortString = gridState.sortString;
    }
    if (this.filterString !== gridState.filter) {
      this.filterString = gridState.filter;
      this.skip = 0;
    }

    if (this.searchValue !== gridState.searchValue) {
      let variants = [];
      this.searchFilterString = '';
      this.searchValue = gridState.searchValue;

      if (this.searchValue !== null) {
        variants = this.searchValue.split(' ').filter(value => value);
      }

      variants.map(variant => {
        if (variant) {
          if (this.searchFilterString.length > 2) {
            this.searchFilterString += ' AND ';
          }
          this.searchFilterString += `(Reviewer.FirstName like "${variant}") OR (Reviewer.LastName like "${variant}")`;
        }
      });

      this.skip = 0;
    }

    this.getRequest.unsubscribe();
    this.getBuySellPlanReviewers();
  }

}
